import Model from "../../core/model/Model";
import { INameTranslation } from "../../core/model/translations/NameTranslation";
import ValidatableObject from "../../core/model/validation/ValidatableObject";
import { TIndexTypeGroup } from "../../indexTypes/model/IndexTypeGroup";
import { IOrganisationIndex } from "./OrganisationIndex";
import { IOrganisationIndexCategory } from "./OrganisationIndexCategory";

export interface TOrganisationIndexGroup {
  id: number;
  name: string;
  indexTypeGroupId: number;
  organisationId: number;
  parentId: number;
  valid: boolean;
  harmonized: boolean;
  groupIds: string;
  organisationIndexCategoryId: number;
  organisationIndexCategory: IOrganisationIndexCategory;
  organisationIndexList: IOrganisationIndex[];
  nameTranslations: INameTranslation[];
}

export interface IOrganisationIndexGroup extends TOrganisationIndexGroup {
  groupType?: TIndexTypeGroup;
  groups?: IOrganisationIndexGroup[];
  children?: IOrganisationIndex[];
  find(id: number): IOrganisationIndexGroup | undefined;
  findIndex(id: number): IOrganisationIndex | undefined;
  contains(criteria: string): boolean;
  isParent(id: number): boolean;
  isSibling(id: number): boolean;
  print(prefix?: string): string;
  addGroup(item: IOrganisationIndexGroup): void;
  addChildren(items?: IOrganisationIndex[]): void;
}

const OrganisationIndexGroup = Model(
  (model: TOrganisationIndexGroup): IOrganisationIndexGroup => {
    const _value: TOrganisationIndexGroup = Object.assign({}, model);
    let _groupType: TIndexTypeGroup;
    let _groups: IOrganisationIndexGroup[] = [];
    let _children: IOrganisationIndex[] = [];
    let _model = {
      get id() {
        return _value.id;
      },
      set id(value) {
        _value.id = value;
      },
      get name() {
        return _value.name;
      },
      set name(value) {
        _value.name = value;
      },
      get indexTypeGroupId() {
        return _value.indexTypeGroupId;
      },
      set indexTypeGroupId(value) {
        _value.indexTypeGroupId = value;
      },
      get organisationId() {
        return _value.organisationId;
      },
      set organisationId(value) {
        _value.organisationId = value;
      },
      get parentId() {
        return _value.parentId;
      },
      set parentId(value) {
        _value.parentId = value;
      },
      get organisationIndexCategoryId() {
        return _value.organisationIndexCategoryId;
      },
      set organisationIndexCategoryId(value) {
        _value.organisationIndexCategoryId = value;
      },
      get organisationIndexList() {
        return _value.organisationIndexList;
      },
      set organisationIndexList(value) {
        _value.organisationIndexList = value;
      },
      get organisationIndexCategory() {
        return _value.organisationIndexCategory;
      },
      set organisationIndexCategory(value) {
        _value.organisationIndexCategory = value;
      },
      get valid() {
        return _value.valid;
      },
      set valid(value) {
        _value.valid = value;
      },
      get groupIds() {
        return _value.groupIds;
      },
      set groupIds(value) {
        _value.groupIds = value;
      },
      get harmonized() {
        return _value.harmonized;
      },
      set harmonized(value) {
        _value.harmonized = value;
      },
      get nameTranslations() {
        return _value.nameTranslations;
      },
      set nameTranslations(value) {
        _value.nameTranslations = value;
      },
      get groupType() {
        return _groupType;
      },
      set groupType(value) {
        _groupType = value;
      },
      get groups() {
        return _groups;
      },
      set groups(value) {
        _groups = value;
      },
      get children() {
        return _children;
      },
      set children(value) {
        _children = value;
      },
      find(id: number) {
        if (_value.id === id) {
          return _validatableModel;
        }
        const _groupIds = _value.groupIds?.split("-");
        if (_groupIds?.includes(id.toString())) {
          return _validatableModel;
        }
        for (let item of _groups) {
          let result = item.find(id);
          if (result) {
            return result;
          }
        }
        return undefined;
      },
      findIndex(id: number) {
        let value = _children?.find((item) => {
          if (item.id === id) {
            return true;
          }
          const _indexIds = item.indexIds?.split("-");
          if (_indexIds?.includes(id.toString())) {
            return true;
          }
          return false;
        });
        if (value) {
          return value;
        }
        for (let item of _groups) {
          let result = item.findIndex(id);
          if (result) {
            return result;
          }
        }
        return undefined;
      },
      print(prefix?: string) {
        let result = "";
        result += `${prefix}${_value.name}\n`;
        for (let item of _groups) {
          result += item.print(prefix + "  ") + "\n";
        }
        for (let item of _children) {
          result += `${prefix + "  "}${item.name}\n`;
        }
        return result;
      },

      contains(criteria: string) {
        if (
          _value.name.toLowerCase().indexOf(criteria.toLowerCase()) !== -1 ||
          _value.nameTranslations.some(
            (item) =>
              item.nameTranslation
                .toLowerCase()
                .indexOf(criteria.toLowerCase()) !== -1
          )
        ) {
          return true;
        }
        if (_children.some((item) => item.contains(criteria))) {
          return true;
        }
        return _groups.some((item) => item.contains(criteria));
      },
      isParent(id: number) {
        if (_value.id === id) {
          return true;
        }
        const _groupIds = _value.groupIds?.split("-");
        if (_groupIds?.includes(id?.toString())) {
          return true;
        }
        return false;
      },
      isSibling(id: number) {
        if (_value.id === id) {
          return true;
        }
        const _groupIds = _value.groupIds?.split("-");
        if (_groupIds?.includes(id.toString())) {
          return true;
        }
        return false;
      },
      addGroup(group: IOrganisationIndexGroup) {
        for (let item of _groups) {
          let result = item.find(group.id);
          if (result) {
            return;
          }
        }
        _groups.push(group);
      },
      addChildren(items?: IOrganisationIndex[]) {
        if (!items) {
          return;
        }

        for (let item of items) {
          let searchResult = _model.findIndex(item.id);
          if (searchResult) {
            continue;
          }
          _children.push(item);
        }
      }
    };

    let _validatableModel = ValidatableObject<IOrganisationIndexGroup>(_model);
    return _validatableModel;
  }
);

export default OrganisationIndexGroup;
